import { Fragment, useEffect, useState } from 'react';
import {
  LEDGER_TRANSACTION_KIND_LABELS, SETTLED_VIA_LABELS, FEE_TRIGGER_LABELS, formatRupiah,
  type LedgerBalanceView, type LedgerTransactionView, type FeeRuleView, type FeeTrigger,
  type LedgerAccountView,
} from 'shared';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../context/AuthContext';

interface WithdrawalRow {
  id: string; accountId: string; accountCode: string; accountName: string;
  amount: string; occurredAt: string; reference: string | null; note: string | null;
  createdByName: string | null;
}

const fmtDateTime = (iso: string | null) => iso
  ? new Date(iso).toLocaleString('id-ID', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' })
  : '—';

/** A debit reads as money in, a credit as money out — spelled out rather than signed. */
function side(amount: string): { label: string; value: string } {
  const v = BigInt(amount);
  return v > 0n
    ? { label: 'Debit', value: formatRupiah(v.toString()) }
    : { label: 'Kredit', value: formatRupiah((-v).toString()) };
}

export function Ledger() {
  const { can } = useAuth();

  const [balances, setBalances] = useState<LedgerBalanceView[]>([]);
  const [transactions, setTransactions] = useState<LedgerTransactionView[]>([]);
  const [withdrawals, setWithdrawals] = useState<WithdrawalRow[]>([]);
  const [accounts, setAccounts] = useState<LedgerAccountView[]>([]);
  const [feeRules, setFeeRules] = useState<FeeRuleView[]>([]);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const [wd, setWd] = useState({ accountId: '', amount: '', reference: '', note: '' });
  const [fee, setFee] = useState<{
    trigger: FeeTrigger; amount: string; shares: { ledgerAccountId: string; amount: string }[];
  }>({ trigger: 'on_order', amount: '', shares: [{ ledgerAccountId: '', amount: '' }] });

  async function loadAll() {
    setLoading(true);
    try {
      const [b, t, w, a, f] = await Promise.all([
        api.get<LedgerBalanceView[]>('/ledger/balances'),
        api.get<LedgerTransactionView[]>('/ledger/transactions?limit=100'),
        can('withdrawal:read') ? api.get<WithdrawalRow[]>('/ledger/withdrawals').catch(() => []) : Promise.resolve([]),
        api.get<LedgerAccountView[]>('/ledger/accounts').catch(() => []),
        can('fee:read') ? api.get<FeeRuleView[]>('/ledger/fees').catch(() => []) : Promise.resolve([]),
      ]);
      setBalances(b);
      setTransactions(t);
      setWithdrawals(w);
      setAccounts(a);
      setFeeRules(f);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Gagal memuat buku besar.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void loadAll(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  async function act(fn: () => Promise<unknown>, ok: string) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await fn();
      setNotice(ok);
      await loadAll();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Tindakan gagal.');
    } finally {
      setBusy(false);
    }
  }

  const cash = balances.find((b) => b.owner === 'platform');
  const liabilities = balances.filter((b) => b.kind === 'liability');
  const owedTotal = liabilities.reduce((sum, b) => sum + BigInt(b.balance), 0n);
  const cashValue = BigInt(cash?.balance ?? '0');
  const reconciles = cashValue === owedTotal;

  const feeShareTotal = fee.shares.reduce((sum, s) => sum + BigInt(s.amount || '0'), 0n);
  const feeAmount = BigInt(fee.amount || '0');

  return (
    <div className="stack">
      <h1>Buku Besar</h1>

      {error && <div className="msg error">{error}</div>}
      {notice && <div className="msg ok">{notice}</div>}

      <div className="card stack">
        <h2>Saldo per pihak</h2>
        {loading ? <p className="muted">Memuat…</p> : (
          <>
            <table>
              <thead>
                <tr>
                  <th>Akun</th>
                  <th style={{ textAlign: 'right' }}>Debit</th>
                  <th style={{ textAlign: 'right' }}>Kredit</th>
                  <th style={{ textAlign: 'right' }}>Saldo</th>
                  <th>Mutasi terakhir</th>
                </tr>
              </thead>
              <tbody>
                {balances.map((b) => (
                  <tr key={b.id}>
                    <td>
                      {b.name}
                      <br />
                      <span className="mono muted" style={{ fontSize: '0.75rem' }}>{b.code}</span>
                    </td>
                    <td className="mono muted" style={{ textAlign: 'right' }}>{formatRupiah(b.totalDebit)}</td>
                    <td className="mono muted" style={{ textAlign: 'right' }}>{formatRupiah(b.totalCredit)}</td>
                    <td className="mono" style={{ textAlign: 'right' }}>
                      <strong>{formatRupiah(b.balance)}</strong>
                    </td>
                    <td className="muted">{fmtDateTime(b.lastMovementAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/*
              The self-check. Cash held must equal everything owed out; if it does not, a
              transaction is unbalanced and the figures above cannot be trusted.
            */}
            <div className={reconciles ? 'msg ok' : 'msg error'}>
              {reconciles
                ? `Kas ${formatRupiah(cashValue.toString())} sama dengan total kewajiban `
                  + `${formatRupiah(owedTotal.toString())} — buku besar seimbang.`
                : `Kas ${formatRupiah(cashValue.toString())} TIDAK sama dengan total kewajiban `
                  + `${formatRupiah(owedTotal.toString())}. Ada transaksi yang tidak seimbang; `
                  + 'hubungi admin AiYO sebelum melakukan pencairan.'}
            </div>
            <p className="muted">
              Saldo dihitung dari baris jurnal, bukan disimpan sebagai kolom. Angka di sini
              tidak bisa berbeda dari baris yang membentuknya.
            </p>
          </>
        )}
      </div>

      {can('withdrawal:write') && (
        <div className="card stack">
          <h2>Catat pencairan</h2>
          <p className="muted">
            Untuk uang yang benar-benar sudah dikirim ke pihak yang bersangkutan. Sistem
            menolak pencairan yang melebihi saldo — saldo minus berarti kita membayarkan uang
            yang belum pernah diterima.
          </p>
          <div className="grid-2">
            <label className="field">
              <span className="label">Akun</span>
              <select value={wd.accountId} onChange={(e) => setWd({ ...wd, accountId: e.target.value })}>
                <option value="">Pilih akun…</option>
                {liabilities.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name} — saldo {formatRupiah(b.balance)}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span className="label">Nominal (rupiah)</span>
              <input
                className="mono" inputMode="numeric" value={wd.amount}
                onChange={(e) => setWd({ ...wd, amount: e.target.value.replace(/\D/g, '') })}
                placeholder="0"
              />
            </label>
            <label className="field">
              <span className="label">Referensi</span>
              <input
                value={wd.reference}
                onChange={(e) => setWd({ ...wd, reference: e.target.value })}
                placeholder="Nomor transfer atau bukti"
              />
            </label>
            <label className="field">
              <span className="label">Catatan</span>
              <input value={wd.note} onChange={(e) => setWd({ ...wd, note: e.target.value })} />
            </label>
          </div>
          <button
            className="primary"
            disabled={busy || !wd.accountId || !wd.amount || wd.amount === '0'}
            onClick={() => void act(async () => {
              await api.post('/ledger/withdrawals', {
                accountId: wd.accountId, amount: wd.amount,
                reference: wd.reference || undefined, note: wd.note || undefined,
              });
              setWd({ accountId: '', amount: '', reference: '', note: '' });
            }, 'Pencairan dicatat.')}
          >
            Catat pencairan
          </button>
        </div>
      )}

      {withdrawals.length > 0 && (
        <div className="card stack">
          <h2>Riwayat pencairan</h2>
          <table>
            <thead>
              <tr>
                <th>Tanggal</th>
                <th>Akun</th>
                <th style={{ textAlign: 'right' }}>Nominal</th>
                <th>Referensi</th>
                <th>Dicatat oleh</th>
              </tr>
            </thead>
            <tbody>
              {withdrawals.map((w) => (
                <tr key={w.id}>
                  <td className="muted">{fmtDateTime(w.occurredAt)}</td>
                  <td>{w.accountName}</td>
                  <td className="mono" style={{ textAlign: 'right' }}>{formatRupiah(w.amount)}</td>
                  <td className="mono muted">{w.reference ?? '—'}</td>
                  <td className="muted">{w.createdByName ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {can('fee:write') && (
        <div className="card stack">
          <h2>Atur biaya transaksi</h2>
          <p className="muted">
            Nominal biaya tidak bisa diubah — mengubah biaya berarti menutup aturan yang
            berjalan dan membuat aturan baru, supaya pesanan lama tetap bisa menjelaskan
            angka yang dulu ditagihkan. Boleh diisi 0.
          </p>
          <div className="grid-2">
            <label className="field">
              <span className="label">Dikenakan</span>
              <select
                value={fee.trigger}
                onChange={(e) => setFee({ ...fee, trigger: e.target.value as FeeTrigger })}
              >
                {(['on_order', 'on_payment'] as FeeTrigger[]).map((t) => (
                  <option key={t} value={t}>{FEE_TRIGGER_LABELS[t]}</option>
                ))}
              </select>
            </label>
            <label className="field">
              <span className="label">Nominal (rupiah)</span>
              <input
                className="mono" inputMode="numeric" value={fee.amount}
                onChange={(e) => setFee({ ...fee, amount: e.target.value.replace(/\D/g, '') })}
                placeholder="0"
              />
            </label>
          </div>

          <span className="label">Pembagian penerima</span>
          {fee.shares.map((s, i) => (
            <div className="grid-2" key={i}>
              <label className="field">
                <span className="label">Penerima</span>
                <select
                  value={s.ledgerAccountId}
                  onChange={(e) => {
                    const shares = [...fee.shares];
                    shares[i] = { ...shares[i]!, ledgerAccountId: e.target.value };
                    setFee({ ...fee, shares });
                  }}
                >
                  <option value="">Pilih akun…</option>
                  {accounts.filter((a) => a.kind === 'liability').map((a) => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span className="label">Bagian (rupiah)</span>
                <input
                  className="mono" inputMode="numeric" value={s.amount}
                  onChange={(e) => {
                    const shares = [...fee.shares];
                    shares[i] = { ...shares[i]!, amount: e.target.value.replace(/\D/g, '') };
                    setFee({ ...fee, shares });
                  }}
                />
              </label>
            </div>
          ))}

          <div className="row">
            <button
              onClick={() => setFee({ ...fee, shares: [...fee.shares, { ledgerAccountId: '', amount: '' }] })}
            >
              Tambah penerima
            </button>
            {fee.shares.length > 1 && (
              <button onClick={() => setFee({ ...fee, shares: fee.shares.slice(0, -1) })}>
                Hapus penerima terakhir
              </button>
            )}
          </div>

          {/* Shown before submitting, because the database refuses an unbalanced split. */}
          <p className={feeShareTotal === feeAmount ? 'muted' : 'danger'}>
            Pembagian {formatRupiah(feeShareTotal.toString())} dari {formatRupiah(feeAmount.toString())}.
            {feeShareTotal !== feeAmount && ' Setiap rupiah biaya harus punya penerima.'}
          </p>

          <button
            className="primary"
            disabled={busy || feeShareTotal !== feeAmount || fee.shares.some((s) => feeAmount > 0n && !s.ledgerAccountId)}
            onClick={() => void act(async () => {
              await api.post('/ledger/fees', {
                trigger: fee.trigger,
                amount: fee.amount || '0',
                recipients: feeAmount === 0n ? [] : fee.shares.filter((s) => s.ledgerAccountId && s.amount),
              });
              setFee({ trigger: fee.trigger, amount: '', shares: [{ ledgerAccountId: '', amount: '' }] });
            }, 'Aturan biaya baru berlaku.')}
          >
            Berlakukan aturan biaya
          </button>
        </div>
      )}

      {feeRules.length > 0 && (
        <div className="card stack">
          <h2>Riwayat aturan biaya</h2>
          <table>
            <thead>
              <tr>
                <th>Dikenakan</th>
                <th style={{ textAlign: 'right' }}>Nominal</th>
                <th>Berlaku</th>
                <th>Pembagian</th>
              </tr>
            </thead>
            <tbody>
              {feeRules.map((r) => (
                <tr key={r.id}>
                  <td>{FEE_TRIGGER_LABELS[r.trigger]}</td>
                  <td className="mono" style={{ textAlign: 'right' }}>{formatRupiah(r.amount)}</td>
                  <td className="muted">
                    {fmtDateTime(r.effectiveFrom)} — {r.effectiveTo ? fmtDateTime(r.effectiveTo) : 'sekarang'}
                  </td>
                  <td className="muted">
                    {r.recipients.length === 0 ? '—' : r.recipients
                      .map((x) => `${x.accountName} ${formatRupiah(x.amount)}`).join(' · ')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="card stack">
        <h2>Jurnal</h2>
        <p className="muted">
          Baris jurnal tidak bisa diubah atau dihapus. Koreksi dilakukan dengan transaksi
          pembalik yang tetap terlihat — buku yang bisa disunting tidak bisa direkonsiliasi
          dengan rekening bank.
        </p>
        {transactions.length === 0 ? <p className="muted">Belum ada transaksi.</p> : (
          <table>
            <thead>
              <tr>
                <th>Tanggal</th>
                <th>Jenis</th>
                <th>Keterangan</th>
                <th>Oleh</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {transactions.map((t) => (
                // A keyed Fragment, not `<>`: a shorthand fragment cannot take a key, and
                // each transaction renders two sibling rows.
                <Fragment key={t.id}>
                  <tr>
                    <td className="muted">{fmtDateTime(t.occurredAt)}</td>
                    <td>
                      <span className={`pill${t.kind === 'adjustment' ? ' accent' : ''}`}>
                        {LEDGER_TRANSACTION_KIND_LABELS[t.kind]}
                      </span>
                    </td>
                    <td>
                      {t.description}
                      {t.settledVia && (
                        <>
                          <br />
                          <span className="muted" style={{ fontSize: '0.75rem' }}>
                            {SETTLED_VIA_LABELS[t.settledVia]}
                          </span>
                        </>
                      )}
                    </td>
                    <td className="muted">{t.actorName ?? '—'}</td>
                    <td>
                      <button onClick={() => setExpanded(expanded === t.id ? null : t.id)}>
                        {expanded === t.id ? 'Tutup' : `${t.entries.length} baris`}
                      </button>
                    </td>
                  </tr>
                  {expanded === t.id && (
                    <tr>
                      <td colSpan={5}>
                        <table>
                          <tbody>
                            {t.entries.map((e) => {
                              const s = side(e.amount);
                              return (
                                <tr key={e.id}>
                                  <td>{e.accountName}</td>
                                  <td className="muted">{e.memo ?? '—'}</td>
                                  <td className="muted" style={{ textAlign: 'right' }}>{s.label}</td>
                                  <td className="mono" style={{ textAlign: 'right' }}>{s.value}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                        {can('ledger:adjust') && t.kind !== 'adjustment' && (
                          <button
                            className="danger"
                            disabled={busy}
                            onClick={() => {
                              const reason = window.prompt('Alasan koreksi:');
                              if (!reason || reason.trim().length < 5) return;
                              void act(
                                () => api.post('/ledger/adjustments', {
                                  reversesTransactionId: t.id, reason,
                                }),
                                'Transaksi pembalik dibuat.',
                              );
                            }}
                          >
                            Buat koreksi pembalik
                          </button>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
